import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface PremiumCredits {
  hasPremium: boolean;
  creditsRemaining: number;
  creditsTotal: number;
  creditsUsed: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

export const usePremiumCredits = (): PremiumCredits => {
  const { user } = useAuth();
  const [hasPremium, setHasPremium] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(0);
  const [creditsTotal, setCreditsTotal] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [loading, setLoading] = useState(true);

  const checkCredits = async () => {
    if (!user) {
      setHasPremium(false);
      setCreditsRemaining(0);
      setCreditsTotal(0);
      setCreditsUsed(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('check-premium-credits');
      
      if (error) throw error;
      
      setHasPremium(data.hasPremium || false);
      setCreditsRemaining(data.creditsRemaining || 0);
      setCreditsTotal(data.creditsTotal || 0);
      setCreditsUsed(data.creditsUsed || 0);
    } catch (error) {
      console.error('Error checking premium credits:', error);
      setHasPremium(false);
      setCreditsRemaining(0);
      setCreditsTotal(0);
      setCreditsUsed(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkCredits();
  }, [user]);

  return {
    hasPremium,
    creditsRemaining,
    creditsTotal,
    creditsUsed,
    loading,
    refresh: checkCredits,
  };
};
